/** @jsxImportSource @emotion/react */
import {
  useTheme,
  Button,
  Typography,
  Snackbar,
  ButtonProps,
} from '@quickbit/qb-design-system';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { CountrySelect, RememberMe } from 'components';
import { useMixpanel } from 'context';
import { useEidLogin } from 'hooks/login';
import { EidProviderName } from 'types';
import { isOnfidoCountry, getErrorMessage } from 'utils';
import { OnrampLayout } from './components';
import { useOnrampData } from './context';
import { useHandleSuccessSignIn } from './hooks/useHandleSuccessSignIn';

export const Login = () => {
  const { spacing } = useTheme();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { mixpanelTrackEvent } = useMixpanel({
    Product: 'TopUp',
    Page: 'Login',
    onMountProps: {
      description: 'User entered login landing page.',
    },
  });
  const handleSuccessSignIn = useHandleSuccessSignIn({
    mixpanelTrackEvent,
  });
  const { existingUser, receiveOrderPreviewId } = useOnrampData();

  const {
    start,
    cancel,
    pollingError,
    isPollingLogin,
    fetchingAuthData,
    appCountries,
    rememberMe,
    setRememberMe,
    changeLoginCountry,
    selectedCountry,
  } = useEidLogin({
    success: handleSuccessSignIn,
    receiveOrderPreviewId: receiveOrderPreviewId,
  });

  useUpdateEffect(() => {
    if (appCountries) {
      if (selectedCountry) {
        if (isOnfidoCountry(selectedCountry.code) && existingUser) {
          navigate('email');
        }
      }
    }
  }, [appCountries]);

  const onLogin = (provider: EidProviderName) => {
    void start({ provider });
    mixpanelTrackEvent('Login started', {
      description: 'User started the login process.',
    });
  };

  const emailAuthMethod = selectedCountry?.auth.find(
    (p) => p.method === 'EMAIL'
  );

  const authMethod = selectedCountry?.auth.filter(
    (p) => p.method !== 'EMAIL'
  )?.[0];

  let buttonProps = undefined;
  if (isPollingLogin) {
    buttonProps = {
      variant: 'secondary',
      text: formatMessage({
        id: 'cancel',
      }),
      onClick: cancel,
    } as ButtonProps;
  } else if (authMethod && authMethod.provider) {
    buttonProps = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onClick: () => onLogin(authMethod.provider!),
      text: formatMessage({
        id: 'page.onramp.login.continue',
      }),
      loading: fetchingAuthData,
      disabled: fetchingAuthData,
    };
  }

  return (
    <OnrampLayout
      buttonProps={buttonProps}
      topChildren={
        <>
          <Typography variant="body">
            {formatMessage({
              id: !isPollingLogin
                ? 'page.onramp.login.verifyInfo'
                : 'page.login.authenticating',
            })}
          </Typography>
          {!isPollingLogin && (
            <div css={{ marginBottom: spacing.xxs }}>
              <CountrySelect
                appCountries={appCountries}
                country={selectedCountry}
                setCountry={(c) => changeLoginCountry(c.code)}
              />
              <RememberMe rememberMe={rememberMe} onChange={setRememberMe} />
            </div>
          )}
        </>
      }
    >
      {!isPollingLogin && (
        <>
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xxxs,
              width: '100%',
            }}
          >
            {emailAuthMethod && (
              <Button
                text={formatMessage({
                  id: 'page.login.button.onfido.login',
                })}
                onClick={() => navigate('email')}
                variant="secondary"
              />
            )}
          </div>
        </>
      )}
      {isPollingLogin && (
        <Button loading disabled css={{ marginBottom: spacing.xxxs }} />
      )}
      {pollingError && (
        <Snackbar
          open={!!pollingError}
          message={getErrorMessage(pollingError)}
          variant="danger"
        />
      )}
    </OnrampLayout>
  );
};
